// sfx.json recipes → sample buffers (pure: no Web Audio, no DOM; the SfxEngine, the tests and `bake:sfx`
// share it). A sound is one recipe variant (`merge.default`, `ui.bonk`, …) plus the derived `merge.delay`
// (the tier ≥ delayMinTier merge with the ZzFX echo, research §4). A randomised sound (some layer draws ZzFX
// `randomness`, default .05) is rendered as `engine.takes` seeded takes (anti-fatigue); a sound whose layers
// all set `randomness: 0` (merge, coin, fanfare, ui.click: musical pitch) would render identical takes, so it
// has one. The vendored ZzFX `noise` is deterministic (sin(i⁵)), not a random source. Layers are mixed at
// their `atMs` offsets.
import type { SfxData } from '../data/schema.ts';
import { hashString, mulberry32 } from './rng.ts';
import { buildSamples } from './vendor/zzfx.ts';
import { toZzfxArray, type ZzfxNamed } from './zzfxParams.ts';

type Recipes = SfxData['recipes'];
export type RecipeKey = keyof Recipes;
type VariantOf<R extends RecipeKey> = keyof Recipes[R]['variants'] & string;

/** Every playable sound: `<recipe>.<variant>`, plus `merge.delay`. */
export type SoundId = { [R in RecipeKey]: `${R}.${VariantOf<R>}` }[RecipeKey] | 'merge.delay';

export const RECIPE_KEYS = [
  'dig',
  'merge',
  'coin',
  'pop',
  'fanfare',
  'ui',
] as const satisfies readonly RecipeKey[];

export interface Layer {
  atMs: number;
  p: Readonly<ZzfxNamed>;
}

export interface SoundDef {
  id: SoundId;
  recipe: RecipeKey;
  variant: string;
  layers: readonly Layer[];
}

/** Every sound of sfx.json in data order (derived `merge.delay` right after `merge.default`). */
export function listSounds(sfx: SfxData): SoundDef[] {
  const out: SoundDef[] = [];
  for (const recipe of RECIPE_KEYS) {
    const variants = sfx.recipes[recipe].variants as Record<string, { layers: Layer[] }>;
    for (const [variant, v] of Object.entries(variants)) {
      out.push({ id: `${recipe}.${variant}` as SoundId, recipe, variant, layers: v.layers });
      if (recipe === 'merge' && variant === 'default') {
        const delay = sfx.recipes.merge.pitch.delayMs / 1000;
        out.push({
          id: 'merge.delay',
          recipe,
          variant: 'delay',
          layers: v.layers.map((l) => ({ atMs: l.atMs, p: { ...l.p, delay } })),
        });
      }
    }
  }
  return out;
}

/** ZzFX draws `rand` only for `randomness` (default .05): a sound with every layer at 0 has one take. */
export function isRandomised(def: SoundDef): boolean {
  return def.layers.some((l) => (l.p.randomness ?? 0.05) !== 0);
}

/** Takes rendered (and baked) for a sound. */
export function takeCount(sfx: SfxData, def: SoundDef): number {
  return isRandomised(def) ? sfx.engine.takes : 1;
}

/** The seed of take `take` (0-based) of a sound: stable across runs, platforms and the Unity port. */
export function takeSeed(id: SoundId, take: number): number {
  return hashString(`${id}#${take}`);
}

/** Mixes the layers at their offsets. ZzFX volume = layer volume × sfx.masterVolume. */
export function renderLayers(
  layers: readonly Layer[],
  sampleRate: number,
  masterVolume: number,
  rand: () => number,
): Float32Array {
  const parts = layers.map((l) => ({
    at: Math.round((l.atMs / 1000) * sampleRate),
    s: buildSamples({ sampleRate, volume: masterVolume, rand }, toZzfxArray(l.p)),
  }));
  const len = parts.reduce((m, p) => Math.max(m, p.at + p.s.length), 1);
  const out = new Float32Array(len);
  for (const p of parts) {
    for (let i = 0; i < p.s.length; i++) out[p.at + i] = (out[p.at + i] ?? 0) + (p.s[i] ?? 0);
  }
  return out;
}

export function renderTake(sfx: SfxData, def: SoundDef, take: number): Float32Array {
  return renderLayers(
    def.layers,
    sfx.sampleRate,
    sfx.masterVolume,
    mulberry32(takeSeed(def.id, take)),
  );
}

/**
 * Renders the takes incrementally (the full v0.1 bank is 25 takes ≈ 0.21 M samples — too slow for one gesture
 * on a phone). Order: take 0 of `engine.unlockSounds`, take 0 of every other sound, then the further takes of the
 * randomised sounds — so an idle prewarm makes every sound playable first. The unlock renders only the
 * unlockSounds' take 0s still missing; any other sound's take 0 renders on its first play (`ensureFirstTakes`).
 * Takes are seeded per (sound, take): the render order never changes a sample.
 */
export interface BankBuilder {
  readonly sampleRate: number;
  readonly sounds: readonly SoundDef[];
  /** Rendered takes of a sound so far, in take order. */
  takes(id: SoundId): readonly Float32Array[];
  /** Takes this sound will have once complete (1 when it draws no randomness). */
  takeCount(id: SoundId): number;
  /** Renders take 0 of each listed sound (default: every sound) that has none yet. */
  ensureFirstTakes(ids?: readonly SoundId[]): void;
  /** Renders the next missing take; false when every take exists. */
  step(): boolean;
  done(): boolean;
  /** Takes still to render (0 = complete). */
  pending(): number;
  /** Samples rendered so far (build-cost diagnostics). */
  samples(): number;
}

export function createBankBuilder(sfx: SfxData): BankBuilder {
  const sounds = listSounds(sfx);
  const byId = new Map<SoundId, SoundDef>(sounds.map((d) => [d.id, d]));
  const takes = new Map<SoundId, Float32Array[]>(sounds.map((d) => [d.id, []]));
  const counts = new Map<SoundId, number>(sounds.map((d) => [d.id, takeCount(sfx, d)]));
  const unlock = new Set<SoundId>(sfx.engine.unlockSounds);
  const order: { def: SoundDef; k: number }[] = [
    ...sounds.filter((d) => unlock.has(d.id)).map((def) => ({ def, k: 0 })),
    ...sounds.filter((d) => !unlock.has(d.id)).map((def) => ({ def, k: 0 })),
  ];
  for (let k = 1; k < sfx.engine.takes; k++) {
    for (const def of sounds) if (k < (counts.get(def.id) ?? 1)) order.push({ def, k });
  }
  const total = order.length;
  let next = 0;
  let rendered = 0;
  let samples = 0;

  const list = (id: SoundId): Float32Array[] => {
    const l = takes.get(id);
    if (!l) throw new Error(`unknown sound ${id}`);
    return l;
  };
  const render = (def: SoundDef, k: number): boolean => {
    const l = list(def.id);
    if (l.length !== k) return false; // already rendered (ensureFirstTakes ran ahead of the prewarm)
    const s = renderTake(sfx, def, k);
    l.push(s);
    rendered += 1;
    samples += s.length;
    return true;
  };

  return {
    sampleRate: sfx.sampleRate,
    sounds,
    takes: (id) => list(id),
    takeCount: (id) => {
      const n = counts.get(id);
      if (n === undefined) throw new Error(`unknown sound ${id}`);
      return n;
    },
    ensureFirstTakes(ids) {
      for (const id of ids ?? sounds.map((d) => d.id)) {
        const def = byId.get(id);
        if (!def) throw new Error(`unknown sound ${id}`);
        render(def, 0);
      }
    },
    step() {
      while (next < total) {
        const o = order[next] as { def: SoundDef; k: number };
        next += 1;
        if (render(o.def, o.k)) return true;
      }
      return false;
    },
    done: () => rendered === total,
    pending: () => total - rendered,
    samples: () => samples,
  };
}

/** Every take of every sound, rendered now (tests, `bake:sfx`). */
export function renderBank(sfx: SfxData): BankBuilder {
  const b = createBankBuilder(sfx);
  while (b.step()) {
    // render everything
  }
  return b;
}

export function peakOf(xs: Float32Array): number {
  let m = 0;
  for (const v of xs) {
    const a = Math.abs(v);
    if (a > m) m = a;
  }
  return m;
}
